import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../../services/api';
import { 
  Box, 
  Container, 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  TextField,
  Stack,
  IconButton,
  alpha,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
  Email as EmailIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { LoginHero } from '../../components/design-system';

const PasswordlessLogin = ({ onLogin }) => {
  const [loginMethod, setLoginMethod] = useState('password'); // 'password' or 'email'
  const [step, setStep] = useState('email'); // 'email' or 'code'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      console.log('📧 Sending verification code...');
      
      const response = await authAPI.sendVerificationCode(email);
      
      console.log('✅ Code sent:', response);
      
      setCodeSent(true);
      setStep('code');
      setCountdown(10 * 60); // 10 minutes
      
      // Start countdown
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
    } catch (error) {
      console.error('❌ Send code error:', error);
      setError(error.message || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      console.log('🔐 Attempting password login...');
      
      // Try login without userType first (let backend determine), then try with userType if needed
      let response;
      try {
        // First attempt: no userType specified - let backend determine the correct type
        response = await authAPI.login({
          email: email,
          password: password
        });
        console.log('✅ Login successful (auto-detected user type)');
      } catch (autoError) {
        // Fallback: try with CLIENT userType (will also work for CLIENT_REP)
        console.log('🔄 Auto-detection failed, trying with CLIENT userType...');
        try {
          response = await authAPI.login({
            email: email,
            password: password,
            userType: 'CLIENT'
          });
          console.log('✅ CLIENT login successful');
        } catch (clientError) {
          // Final fallback: try INTERNAL
          console.log('🔄 CLIENT login failed, trying INTERNAL...');
          response = await authAPI.login({
            email: email,
            password: password,
            userType: 'INTERNAL'
          });
          console.log('✅ INTERNAL login successful');
        }
      }
      
      console.log('✅ Login response:', response);

      // Store token in localStorage (required for API calls)
      if (response.tokens && response.tokens.accessToken) {
        localStorage.setItem('authToken', response.tokens.accessToken);
        console.log('✅ Auth token stored in localStorage:', response.tokens.accessToken.substring(0, 30) + '...');
      }

      if (response.tokens && response.tokens.refreshToken) {
        localStorage.setItem('refreshToken', response.tokens.refreshToken);
      }

      // Build user object from response
      // Normalize userType: CLIENT_REP -> rep for routing
      let normalizedUserType = response.user.userType.toLowerCase();
      if (normalizedUserType === 'client_rep') {
        normalizedUserType = 'rep';
      }
      
      const user = {
        id: response.user.id,
        name: `${response.user.firstName} ${response.user.lastName}`,
        email: response.user.email,
        firstName: response.user.firstName,
        lastName: response.user.lastName,
        userType: normalizedUserType,
        organization: response.user.organization,
        isSuperAdmin: response.user.isSuperAdmin || false
      };

      console.log('✅ User object created:', user);
      
      // Store user in localStorage for persistence
      localStorage.setItem('currentUser', JSON.stringify(user));

      // Call parent onLogin handler
      onLogin(user);
      setLoading(false);

    } catch (error) {
      console.error('❌ Password login error:', error);
      setError(error.message || 'Invalid email or password');
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      console.log('🔐 Verifying code...');
      
      const response = await authAPI.verifyCode(email, verificationCode);
      
      console.log('✅ Login response:', response);

      // Store token in localStorage (required for API calls)
      if (response.tokens && response.tokens.accessToken) {
        localStorage.setItem('authToken', response.tokens.accessToken);
        console.log('✅ Auth token stored in localStorage:', response.tokens.accessToken.substring(0, 30) + '...');
      }

      if (response.tokens && response.tokens.refreshToken) {
        localStorage.setItem('refreshToken', response.tokens.refreshToken);
      }

      // Build user object from response
      // Normalize userType: CLIENT_REP -> rep for routing
      let normalizedUserType = response.user.userType.toLowerCase();
      if (normalizedUserType === 'client_rep') {
        normalizedUserType = 'rep';
      }
      
      const user = {
        id: response.user.id,
        name: `${response.user.firstName} ${response.user.lastName}`,
        email: response.user.email,
        firstName: response.user.firstName,
        lastName: response.user.lastName,
        userType: normalizedUserType,
        organization: response.user.organization,
        isSuperAdmin: response.user.isSuperAdmin || false
      };

      console.log('✅ User object created:', user);
      
      // Store user in localStorage for persistence
      localStorage.setItem('currentUser', JSON.stringify(user));

      // Call parent onLogin handler
      onLogin(user);
      setLoading(false);

    } catch (error) {
      console.error('❌ Verify code error:', error);
      setError(error.message || 'Invalid verification code');
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setLoading(true);
    
    try {
      const response = await authAPI.sendVerificationCode(email);
      setCountdown(10 * 60); // Reset countdown
      
      // Start countdown
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
    } catch (error) {
      setError(error.message || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <LoginHero>
      <Container maxWidth="sm">
        {/* Home Button */}
        <Box sx={{ position: 'absolute', top: 20, left: 20 }}>
          <IconButton
            component={Link}
            to="/"
            sx={{
              color: 'white',
              bgcolor: alpha('#ffffff', 0.1),
              backdropFilter: 'blur(10px)',
              '&:hover': {
                bgcolor: alpha('#ffffff', 0.2),
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            <HomeIcon />
          </IconButton>
        </Box>
        
        <Card sx={{ 
          p: 4,
          backdropFilter: 'blur(20px)',
          bgcolor: alpha('#ffffff', 0.95),
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          border: `1px solid ${alpha('#ffffff', 0.2)}`
        }}>
          <CardContent>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography 
                variant="h3" 
                component="h2" 
                sx={{ 
                  fontWeight: 700, 
                  color: '#0f172a',
                  mb: 1 
                }}
              >
                {loginMethod === 'password' 
                  ? 'Welcome Back' 
                  : step === 'email' 
                    ? 'Email Login' 
                    : 'Enter Code'
                }
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ color: '#475569' }}
              >
                {loginMethod === 'password'
                  ? 'Sign in with your password'
                  : step === 'email' 
                    ? 'Enter your email to receive a verification code' 
                    : `We sent a 6-digit code to ${email}`
                }
              </Typography>
            </Box>

            {/* Login Method Toggle */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <Box sx={{ 
                display: 'flex', 
                bgcolor: '#f1f5f9', 
                borderRadius: 2, 
                p: 0.5 
              }}>
                <Button
                  variant={loginMethod === 'password' ? 'contained' : 'text'}
                  onClick={() => {
                    setLoginMethod('password');
                    setStep('email');
                    setError('');
                  }}
                  sx={{
                    borderRadius: 1.5,
                    px: 3,
                    py: 1,
                    minWidth: 120,
                    bgcolor: loginMethod === 'password' ? '#3b82f6' : 'transparent',
                    color: loginMethod === 'password' ? 'white' : '#64748b',
                    '&:hover': {
                      bgcolor: loginMethod === 'password' ? '#2563eb' : '#e2e8f0'
                    }
                  }}
                >
                  Password
                </Button>
                <Button
                  variant={loginMethod === 'email' ? 'contained' : 'text'}
                  onClick={() => {
                    setLoginMethod('email');
                    setStep('email');
                    setError('');
                  }}
                  sx={{
                    borderRadius: 1.5,
                    px: 3,
                    py: 1,
                    minWidth: 120,
                    bgcolor: loginMethod === 'email' ? '#3b82f6' : 'transparent',
                    color: loginMethod === 'email' ? 'white' : '#64748b',
                    '&:hover': {
                      bgcolor: loginMethod === 'email' ? '#2563eb' : '#e2e8f0'
                    }
                  }}
                >
                  Email Code
                </Button>
              </Box>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {loginMethod === 'password' ? (
              <Box component="form" onSubmit={handlePasswordLogin}>
                <Stack spacing={3}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    InputProps={{
                      startAdornment: <EmailIcon sx={{ mr: 1, color: '#64748b' }} />
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#3b82f6'
                        }
                      }
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Password"
                    name="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    InputProps={{
                      startAdornment: <SecurityIcon sx={{ mr: 1, color: '#64748b' }} />
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#3b82f6'
                        }
                      }
                    }}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={loading || !email || !password}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      bgcolor: '#10b981',
                      '&:hover': {
                        bgcolor: '#059669'
                      }
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Sign In'
                    )}
                  </Button>
                </Stack>
              </Box>
            ) : step === 'email' ? (
              <Box component="form" onSubmit={handleSendCode}>
                <Stack spacing={3}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    InputProps={{
                      startAdornment: <EmailIcon sx={{ mr: 1, color: '#64748b' }} />
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#3b82f6'
                        }
                      }
                    }}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={loading || !email}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      bgcolor: '#3b82f6',
                      '&:hover': {
                        bgcolor: '#2563eb'
                      }
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Send Verification Code'
                    )}
                  </Button>
                </Stack>
              </Box>
            ) : (
              <Box component="form" onSubmit={handleVerifyCode}>
                <Stack spacing={3}>
                  <TextField
                    fullWidth
                    label="Verification Code"
                    name="verificationCode"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="123456"
                    required
                    inputProps={{ 
                      maxLength: 6,
                      style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' }
                    }}
                    InputProps={{
                      startAdornment: <SecurityIcon sx={{ mr: 1, color: '#64748b' }} />
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#3b82f6'
                        }
                      }
                    }}
                  />

                  {countdown > 0 && (
                    <Typography variant="body2" sx={{ textAlign: 'center', color: '#64748b' }}>
                      Code expires in {formatTime(countdown)}
                    </Typography>
                  )}

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={loading || verificationCode.length !== 6}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      bgcolor: '#10b981',
                      '&:hover': {
                        bgcolor: '#059669'
                      }
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Verify & Login'
                    )}
                  </Button>

                  <Divider sx={{ my: 2 }} />

                  <Stack direction="row" spacing={2}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => setStep('email')}
                      startIcon={<ArrowBackIcon />}
                      sx={{
                        borderRadius: 2,
                        borderColor: '#d1d5db',
                        color: '#374151',
                        '&:hover': {
                          borderColor: '#9ca3af',
                          bgcolor: '#f9fafb'
                        }
                      }}
                    >
                      Back to Email
                    </Button>

                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={handleResendCode}
                      disabled={loading || countdown > 0}
                      sx={{
                        borderRadius: 2,
                        borderColor: '#d1d5db',
                        color: '#374151',
                        '&:hover': {
                          borderColor: '#9ca3af',
                          bgcolor: '#f9fafb'
                        }
                      }}
                    >
                      {countdown > 0 ? formatTime(countdown) : 'Resend Code'}
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            )}

            {/* Development Helper */}
            {process.env.NODE_ENV === 'development' && codeSent && (
              <Alert severity="info" sx={{ mt: 3 }}>
                <Typography variant="body2">
                  <strong>Development Mode:</strong> Check the backend console for the verification code.
                </Typography>
              </Alert>
            )}

            {/* Demo Credentials Helper */}
            {process.env.NODE_ENV === 'development' && loginMethod === 'password' && (
              <Alert severity="info" sx={{ mt: 3 }}>
                <Typography variant="body2">
                  <strong>Demo Credentials:</strong><br/>
                  <strong>Admin:</strong> <code>admin@barebiologics.com</code> / <code>Demo123!</code><br/>
                  <strong>EPL Client:</strong> <code>dr.johnson@wellnessmedical.com</code> / <code>Demo123!</code>
                </Typography>
              </Alert>
            )}
          </CardContent>
        </Card>
      </Container>
    </LoginHero>
  );
};

export default PasswordlessLogin;
